import CRMNavigation from "@/components/CRMNavigation";
import Footer from "@/components/Footer";
import Reveal from "@/components/Reveal";

export default function WhatIsCrmPage() {
  return (
    <div className="min-h-screen bg-background" style={{ fontFamily: "'Roboto', sans-serif" }}>
      <CRMNavigation />

      {/* ================= HERO (DARK) ================= */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden bg-[#040814] text-white py-24">
        <div className="absolute inset-0">
          <div className="absolute top-1/3 left-1/4 w-[450px] h-[450px] bg-blue-500/20 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-[350px] h-[350px] bg-indigo-500/20 rounded-full blur-3xl"></div>
        </div>

        <div className="container mx-auto px-6 relative z-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <Reveal variant="up" delay={0}>
                <h1 className="text-4xl md:text-5xl font-extrabold leading-tight">
                  What is CRM?
                </h1>
              </Reveal>

              <Reveal variant="up" delay={80}>
                <p className="mt-5 text-white/80 leading-relaxed">
                  CRM stands for Customer Relationship Management. It's a software solution that makes it easy to store data. You can store all your data and customers, calls, emails, reports, meetings, add notes, manage your business, and find out who's currently using or last used your CRM—without logging out of the CRM system.
                </p>
              </Reveal>

              <Reveal variant="up" delay={140}>
                <a
                  href="/getintouch"
                  className="inline-flex items-center gap-2 mt-8 rounded-xl bg-white px-6 py-3 font-semibold text-[#184fba] hover:bg-blue-50 transition"
                >
                  Contact Us
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </a>
              </Reveal>
            </div>

            <Reveal variant="right" delay={120}>
              <div className="relative">
                <div className="rounded-3xl bg-white/5 backdrop-blur-xl p-4 ring-1 ring-white/10 shadow-2xl">
                  <img
                    src="/Crm.png"
                    alt="CRM Illustration"
                    className="rounded-2xl w-full"
                  />
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ================= WHY CRM (LIGHT) ================= */}
      <section id="crm-software" className="py-20 bg-white">
        <div className="container mx-auto px-6">
          {/* Heading */}
          <div className="max-w-3xl mx-auto text-center">
            <Reveal as="h2" variant="up" delay={0} className="text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900">
              Why businesses choose CRM software
            </Reveal>
            <Reveal as="p" variant="up" delay={80} className="mt-4 text-slate-600 leading-relaxed">
              In these days CRM is a need of every business to operate tasks like lead management, sales lead generation services, lead tracking software, lead follow-up, and many more. Companies building the best CRM in India today serve clients across almost every industry sector.
            </Reveal>
          </div>

          {/* Content */}
          <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            {/* Image (SMALL + FULL VISIBLE) */}
            <div className="lg:col-span-7 relative flex justify-center">
              <div className="absolute -inset-6 rounded-[28px] bg-gradient-to-br from-[#eef6ff] via-[#eef6ff]/40 to-transparent blur-2xl" />
              <div className="relative rounded-[28px] border border-slate-200 bg-white p-4 shadow-[0_30px_80px_-60px_rgba(24,79,186,0.45)]">
                <img
                  src="/data-collection.jpg"
                  alt="CRM diagram"
                  className="mx-auto w-full max-w-xl rounded-2xl object-contain h-64 sm:h-80 md:h-[420px] lg:h-[460px]"
                />
              </div>
            </div>

            {/* Benefits */}
            <div className="lg:col-span-5">
              <div className="rounded-[28px] border border-slate-200 bg-gradient-to-b from-white to-[#f8fbff] p-6 md:p-8 shadow-soft">
                <Reveal as="h3" variant="up" delay={80} className="text-xl font-extrabold text-slate-900">
                  What you get instantly
                </Reveal>

                <div className="mt-6 space-y-4">
                  {[
                    {
                      title: "Organized lead pipeline",
                      desc: "Capture, assign, and move leads with complete clarity across your team.",
                    },
                    {
                      title: "Faster follow-ups",
                      desc: "Track calls, emails, meetings, notes and conversations from one place.",
                    },
                    {
                      title: "Clear performance insights",
                      desc: "Understand what’s working with reports that actually help you grow.",
                    },
                  ].map((item, idx) => (
                    <Reveal key={idx} as="div" variant="up" delay={120 + idx * 80} className="flex gap-3">
                      <div className="mt-1 h-9 w-9 shrink-0 rounded-xl bg-[#eef6ff] text-[#184fba] flex items-center justify-center border border-[#dbeaff]">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-7.5 7.5a1 1 0 01-1.414 0l-3.5-3.5a1 1 0 011.414-1.414L8.5 12.086l6.793-6.793a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900">{item.title}</p>
                        <p className="text-sm text-slate-600 leading-relaxed">
                          {item.desc}
                        </p>
                      </div>
                    </Reveal>
                  ))}
                </div>

                {/* CTA */}
                <div className="mt-8 flex flex-col sm:flex-row gap-3">
                  <a
                    href="/getintouch"
                    className="inline-flex items-center justify-center rounded-2xl bg-[#184fba] px-6 py-3 font-semibold text-white hover:bg-[#1343a1] transition shadow-[0_16px_50px_-22px_rgba(24,79,186,0.8)]"
                  >
                    Talk to us
                  </a>
                  <a
                    href="#uses-of-crm"
                    className="inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-white px-6 py-3 font-semibold text-slate-900 hover:bg-slate-50 transition"
                  >
                    See Use-cases
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

     {/* ================= USES OF CRM (DARK – IMAGE STYLE CONTENT) ================= */}
<section id="uses-of-crm" className="relative py-20 bg-[#040814] text-white">
  {/* Background effects */}
  <div className="absolute inset-0">
    <div className="absolute inset-0 opacity-70 bg-[radial-gradient(ellipse_at_top,rgba(94,162,255,0.18),transparent_60%),radial-gradient(ellipse_at_bottom,rgba(42,124,255,0.12),transparent_60%)]" />
    <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.05)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.05)_1px,transparent_1px)] bg-[size:64px_64px] opacity-[0.12]" />
  </div>

  <div className="container mx-auto px-6 relative z-10">
    {/* Heading */}
    <div className="text-center max-w-2xl mx-auto">
      <Reveal as="h2" variant="up" delay={0} className="text-3xl md:text-4xl font-extrabold">
        Uses of CRM
      </Reveal>
      <Reveal as="p" variant="up" delay={80} className="mt-2 text-white/70">
        Within Your Team
      </Reveal>
    </div>

    {/* Top Cards */}
    <div className="mt-12 grid gap-8 md:grid-cols-3">

      {/* Marketing */}
      <Reveal variant="up" delay={120}>
        <div className="rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur hover:bg-white/10 transition">
          <div className="mb-4 h-10 w-10 flex items-center justify-center rounded-lg bg-white/10">
            <img src="/Marketing-Team.png" alt="" loading="lazy" decoding="async" className="h-6 w-6 invert brightness-0 contrast-200" />
          </div>
          <h3 className="text-lg font-semibold">
            Marketing Team
          </h3>
          <p className="mt-3 text-sm text-white/75 leading-relaxed">
            Collecting information about users visiting websites using web platforms and chat applications, setting up campaigns, and recognizing ROI of marketing activities are key uses of CRM for marketing teams.
          </p>
        </div>
      </Reveal>

      {/* Sales */}
      <Reveal variant="up" delay={160}>
        <div className="rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur hover:bg-white/10 transition">
          <div className="mb-4 h-10 w-10 flex items-center justify-center rounded-lg bg-white/10">
            <img src="/marketing-sales.png" alt="" loading="lazy" decoding="async" className="h-6 w-6 invert brightness-0 contrast-200" />
          </div>
          <h3 className="text-lg font-semibold">
            Sales Team / Sales Management CRM
          </h3>
          <p className="mt-3 text-sm text-white/75 leading-relaxed">
            Identify quality leads, explore past discussions, evaluate monthly sales, track calls and emails, and follow up efficiently with CRM systems built for sales teams.
          </p>
        </div>
      </Reveal>

      {/* Customer Service */}
      <Reveal variant="up" delay={200}>
        <div className="rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur hover:bg-white/10 transition">
          <div className="mb-4 h-10 w-10 flex items-center justify-center rounded-lg bg-white/10">
            <img src="/Customer-Communication.png" alt="" loading="lazy" decoding="async" className="h-6 w-6 invert brightness-0 contrast-200" />
          </div>
          <h3 className="text-lg font-semibold">
            Customer Service Team
          </h3>
          <p className="mt-3 text-sm text-white/75 leading-relaxed">
            Integrating CRM with a helpdesk system provides insights into customer issues before upselling or making strategic decisions.
          </p>
        </div>
      </Reveal>
    </div>

    {/* Divider */}
    <div className="mt-16 flex items-center justify-center">
      <div className="h-px w-24 bg-white/15" />
      <span className="mx-4 text-white/70 font-semibold">
        Based on Company Size
      </span>
      <div className="h-px w-24 bg-white/15" />
    </div>

    {/* Bottom Cards */}
    <div className="mt-10 grid gap-8 md:grid-cols-3">

      {/* Small Business */}
      <Reveal variant="up" delay={220}>
        <div className="rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur hover:bg-white/10 transition">
          <div className="mb-4 h-10 w-10 flex items-center justify-center rounded-lg bg-white/10">
            <img src="/crm-small-bussines.png" alt="" loading="lazy" decoding="async" className="h-6 w-6 invert brightness-0 contrast-200" />
          </div>
          <h3 className="text-lg font-semibold">
            CRM for Small Business
          </h3>
          <p className="mt-3 text-sm text-white/75 leading-relaxed">
            Even small businesses can benefit from CRM tools to organize leads, manage customers, and prepare for scalable growth.
          </p>
        </div>
      </Reveal>

      {/* Startups */}
      <Reveal variant="up" delay={240}>
        <div className="rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur hover:bg-white/10 transition">
          <div className="mb-4 h-10 w-10 flex items-center justify-center rounded-lg bg-white/10">
            <img src="/CRM-Startups.png" alt="" loading="lazy" decoding="async" className="h-6 w-6 invert brightness-0 contrast-200" />
          </div>
          <h3 className="text-lg font-semibold">
            CRM for Startups
          </h3>
          <p className="mt-3 text-sm text-white/75 leading-relaxed">
            CRM for startups helps manage contacts, calls, emails, reports, and gives a clear business overview with easy-to-use features.
          </p>
        </div>
      </Reveal>

      {/* Enterprise */}
      <Reveal variant="up" delay={260}>
        <div className="rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur hover:bg-white/10 transition">
          <div className="mb-4 h-10 w-10 flex items-center justify-center rounded-lg bg-white/10">
            <img src="/Enterprises-developed.png" alt="" loading="lazy" decoding="async" className="h-6 w-6 invert brightness-0 contrast-200" />
          </div>
          <h3 className="text-lg font-semibold">
            Enterprise CRM Solutions
          </h3>
          <p className="mt-3 text-sm text-white/75 leading-relaxed">
            Enterprise CRM systems bring structure, visibility, and control to large sales processes for better execution and governance.
          </p>
        </div>
      </Reveal>

    </div>
  </div>
</section>

      {/* ================= CTA (LIGHT with Premium Gradient) ================= */}
      <section className="relative overflow-hidden py-20 bg-white">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-[#184fba]/10 via-[#2a7cff]/10 to-[#5ea2ff]/10" />
          <div className="absolute -top-24 -right-24 h-96 w-96 rounded-full bg-[#2a7cff]/15 blur-3xl" />
          <div className="absolute -bottom-24 -left-24 h-96 w-96 rounded-full bg-indigo-400/10 blur-3xl" />
        </div>

        <div className="container mx-auto px-6 relative z-10">
          <div className="mx-auto max-w-4xl rounded-[32px] border border-slate-200 bg-white/80 backdrop-blur p-8 md:p-12 shadow-[0_30px_90px_-60px_rgba(24,79,186,0.55)] text-center">
            <h3 className="text-3xl md:text-4xl font-extrabold text-slate-900">Wonder what&apos;s next?</h3>
            <p className="mt-3 text-lg md:text-xl text-slate-600">
              Start your CRM journey with Variable Soft
            </p>

            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                id="contact"
                href="mailto:info@variablesoft.com"
                className="inline-flex w-full sm:w-auto items-center justify-center rounded-2xl bg-[#184fba] px-7 py-3 font-semibold text-white hover:bg-[#1343a1] transition shadow-[0_16px_55px_-25px_rgba(24,79,186,0.85)]"
              >
                Contact Us
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
